import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Building2, X, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 92o++++++++N+ — soft Dashboard nudge that surfaces ONLY when
// the user has 3+ untagged lots. Prompts them to spend a moment
// tagging brokerages so the Tax Account Audit's by-broker rollup
// becomes useful. Mirrors the NIIT onboarding nudge visual DNA
// (soft violet, dismissable, low urgency) so the two don't compete.
//
// Gates:
//   1. Authenticated
//   2. User has at least one portfolio position
//   3. /portfolio/broker-coverage reports 3+ untagged_lots
//   4. Not dismissed permanently (`broker_tagging_nudge_dismissed`)
//
// The 3-lot threshold is intentional: users with 1–2 untagged lots
// might just have a single brokerage and not care; the nudge becomes
// relevant when there's enough volume that bulk tagging saves time.

export default function BrokerTaggingNudge({ hasPositions = false }) {
  const [show, setShow] = useState(false);
  const [untagged, setUntagged] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!hasPositions) return;
    let cancelled = false;
    (async () => {
      try {
        const [covRes, dismissRes] = await Promise.all([
          axios.get(`${API}/portfolio/broker-coverage`, { withCredentials: true }),
          axios.get(`${API}/users/me/dashboard-dismissals`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        const cov = covRes.data || {};
        const dismissals = (dismissRes.data || {}).dismissals || {};
        if (dismissals.broker_tagging_nudge_dismissed) return;
        const u = Number(cov.untagged_lots) || 0;
        const t = Number(cov.total_lots) || 0;
        // 3+ untagged-lots threshold. Also skip if EVERY lot is
        // untagged (likely a brand-new user — let them onboard first).
        if (u < 3) return;
        if (t > 0 && u === t && t < 5) return;
        setUntagged(u);
        setTotal(t);
        setShow(true);
      } catch {
        // anon / unauth — silently skip
      }
    })();
    return () => { cancelled = true; };
  }, [hasPositions]);

  const handleDismiss = async () => {
    setShow(false);
    try {
      await axios.post(
        `${API}/users/me/dashboard-dismissals`,
        { key: 'broker_tagging_nudge_dismissed', value: true },
        { withCredentials: true },
      );
    } catch {
      // ignore — banner is already hidden in this session
    }
  };

  if (!show) return null;

  return (
    <div
      className="relative rounded-xl border border-violet-300/60 dark:border-violet-700/40 bg-gradient-to-r from-violet-50/70 via-purple-50/50 to-violet-50/70 dark:from-violet-950/30 dark:via-purple-950/25 dark:to-violet-950/30 p-3 mb-4"
      data-testid="broker-tagging-nudge"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        title="Dismiss permanently"
        className="absolute top-2 right-2 p-1 rounded-md hover:bg-violet-200/50 dark:hover:bg-violet-800/30 text-violet-700 dark:text-violet-300"
        data-testid="broker-tagging-nudge-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="shrink-0 w-7 h-7 rounded-full bg-violet-200/70 dark:bg-violet-800/40 flex items-center justify-center">
          <Building2 className="w-4 h-4 text-violet-700 dark:text-violet-300" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-violet-900 dark:text-violet-100">
            Tag the brokerages on your lots
          </p>
          <p className="text-[11px] text-violet-800 dark:text-violet-200/90 mt-0.5 leading-snug">
            You have <strong>{untagged}</strong> untagged lot{untagged === 1 ? '' : 's'}
            {total > 0 && (
              <> out of <strong>{total}</strong></>
            )}. Tag the brokerage where each one lives and your{' '}
            <strong>Tax Account Audit</strong> will roll up a clean per-broker breakdown.
            If your whole portfolio sits at one brokerage, click any lot&rsquo;s broker pill and use{' '}
            <em>&ldquo;Change EVERY lot in my portfolio&rdquo;</em> — done in two taps.
          </p>
          <Link
            to="/tax-account-audit"
            className="inline-flex items-center gap-1 px-2.5 py-1 mt-2 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold shadow-sm transition-colors"
            data-testid="broker-tagging-nudge-cta"
          >
            See my Tax Account Audit <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
